import {
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const CMD_IDENT = 0x80
const CMD_HANGUP = 0x81
const CMD_READ_CONFIG = 0x82
const CMD_WRITE_CONFIG = 0x83
const MEMORY_SIZE = 0x8000
const CHANNEL_START = 0x0a00
const CHANNEL_SIZE = 16
const NAME_START = 0x4900
const NAME_SIZE = 8
const VALID_START = 0x7000
const CHANNEL_COUNT = 999
const MEM_VALID = 0xfc
const CHAN_VALID = 0x41
const CHAN_INVALID = 0xfc
const WRITE_RANGES = [
  [0x0a00, 64, 249],
  [0x4840, 48, 1],
  [0x4900, 32, 249],
  [0x6820, 24, 1],
  [0x7000, 8, 124],
  [0x73e0, 1, 7],
] as const

type KGUV9DSpec = {
  id: string
  model: string
  summary: string
}

export type KGUV9DProfile = {
  protocol: 'wouxun-kguv9d'
  id: string
  vendor: 'Wouxun'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 19200
  channelMinimum: 1
  channelMaximum: number
  nameLength: 8
  memorySize: number
  recordStart: number
  powerLevels: readonly ['Low', 'Medium', 'High']
}

const SPECS: readonly KGUV9DSpec[] = [
  {
    id: 'wouxun-kg-uv9d-plus',
    model: 'KG-UV9D Plus',
    summary: 'Wouxun KG-UV9D Plus wideband handheld.',
  },
  {
    id: 'wouxun-kg-uv9g-pro',
    model: 'KG-UV9G Pro',
    summary: 'Wouxun KG-UV9G Pro GMRS handheld.',
  },
  {
    id: 'wouxun-kg-uv9gx',
    model: 'KG-UV9GX',
    summary: 'Wouxun KG-UV9GX expanded GMRS handheld.',
  },
  {
    id: 'wouxun-kg-uv9k',
    model: 'KG-UV9K',
    summary: 'Wouxun KG-UV9K wideband handheld.',
  },
  {
    id: 'wouxun-kg-uv9px',
    model: 'KG-UV9PX',
    summary: 'Wouxun KG-UV9PX wideband handheld.',
  },
]

export const KGUV9D_PROFILES: readonly KGUV9DProfile[] = SPECS.map((spec) => ({
  protocol: 'wouxun-kguv9d',
  id: spec.id,
  vendor: 'Wouxun',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '999 channels', '8-character names'],
  baudRate: 19200,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 8,
  memorySize: MEMORY_SIZE,
  recordStart: 0x7d,
  powerLevels: ['Low', 'Medium', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function readUint16BE(raw: Uint8Array, offset: number): number {
  return (raw[offset] << 8) | raw[offset + 1]
}

function writeUint16BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 8) & 0xff
  raw[offset + 1] = value & 0xff
}

function readUint32BE(raw: Uint8Array, offset: number): number {
  return (
    ((raw[offset] << 24) |
      (raw[offset + 1] << 16) |
      (raw[offset + 2] << 8) |
      raw[offset + 3]) >>>
    0
  )
}

function writeUint32BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 24) & 0xff
  raw[offset + 1] = (value >>> 16) & 0xff
  raw[offset + 2] = (value >>> 8) & 0xff
  raw[offset + 3] = value & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x8000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x01ff).toString(8)),
      polarity: value & 0x4000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value = (Number.parseInt(channel.dtcs.toString(), 8) & 0x01ff) | 0x8000
    if (channel.dtcsPolarity === 'R') value |= 0x4000
    return value
  }
  return 0
}

function decodeName(image: Uint8Array, number: number): string {
  const start = NAME_START + (number - 1) * NAME_SIZE
  return String.fromCharCode(
    ...image.slice(start, start + NAME_SIZE),
  ).replace(/[\u0000\ufffd]/g, '').trim()
}

export function parseKGUV9DChannel(
  image: Uint8Array,
  profile: KGUV9DProfile,
  number: number,
): WebChannel {
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const rxValue = readUint32BE(raw, 0)
  if (rxValue === 0xffffffff || rxValue > 99_999_999) {
    return emptyChannel(number)
  }
  const txValue = readUint32BE(raw, 4)
  const txTone = decodeTone(readUint16BE(raw, 8))
  const rxTone = decodeTone(readUint16BE(raw, 10))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  const mode = raw[13] & 0x04 ? 'AM' : raw[13] & 0x03 ? 'NFM' : 'FM'
  return {
    number,
    empty: false,
    name: decodeName(image, number),
    rxFrequency: rxValue * 10,
    txFrequency: txValue === 0xffffffff ? null : txValue * 10,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode,
    power: profile.powerLevels[(raw[13] >> 3) & 0x03] ?? 'Low',
    skip: (raw[13] & 0x40) === 0,
  }
}

export function parseKGUV9DChannels(
  image: Uint8Array,
  profile: KGUV9DProfile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseKGUV9DChannel(image, profile, index + 1),
  )
}

export function applyKGUV9DChannel(
  image: Uint8Array,
  profile: KGUV9DProfile,
  channel: WebChannel,
): void {
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  const nameStart = NAME_START + (channel.number - 1) * NAME_SIZE
  const valid = VALID_START + channel.number - 1
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0, nameStart, nameStart + NAME_SIZE)
    raw[14] = 0xff
    image[valid] = CHAN_INVALID
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  writeUint32BE(raw, 0, Math.round(channel.rxFrequency / 10))
  writeUint32BE(
    raw,
    4,
    channel.txFrequency ? Math.round(channel.txFrequency / 10) : 0xffffffff,
  )
  const tone = encodeTone(channel)
  writeUint16BE(raw, 8, tone)
  writeUint16BE(raw, 10, tone)
  raw[12] = raw[12] & 0xe3
  raw[13] =
    (raw[13] & 0x80) |
    (channel.skip ? 0 : 0x40) |
    ((
      Math.max(
        0,
        profile.powerLevels.indexOf(
          channel.power as (typeof profile.powerLevels)[number],
        ),
      ) & 0x03
    ) << 3) |
    (channel.mode === 'AM' ? 0x04 : channel.mode === 'NFM' ? 0x01 : 0)
  raw[14] = MEM_VALID
  image.fill(0, nameStart, nameStart + NAME_SIZE)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 8)
  for (let index = 0; index < name.length; index += 1) {
    image[nameStart + index] = name.charCodeAt(index)
  }
  image[valid] = CHAN_VALID
}

function checksum4(data: Uint8Array): number {
  return data.reduce((sum, value) => (sum + value) & 0xff, 0) & 0x0f
}

function obfuscate(data: Uint8Array): Uint8Array {
  const result = new Uint8Array(data.length)
  let previous = 0x52
  for (let index = 0; index < data.length; index += 1) {
    result[index] = previous ^ data[index]
    previous = result[index]
  }
  return result
}

async function writeRecord(
  profile: KGUV9DProfile,
  transport: RadioTransport,
  command: number,
  payload: Uint8Array = new Uint8Array(0),
): Promise<void> {
  const header = Uint8Array.of(profile.recordStart, command, 0xff, payload.length)
  const plain = new Uint8Array(payload.length + 1)
  plain.set(payload)
  plain[payload.length] = checksum4(
    Uint8Array.from([...header.slice(1), ...payload]),
  )
  await transport.write(new Uint8Array([...header, ...obfuscate(plain)]))
}

async function readRecord(
  profile: KGUV9DProfile,
  transport: RadioTransport,
): Promise<{ command: number; payload: Uint8Array }> {
  const header = await transport.readExact(4, 3500)
  if (header[0] !== profile.recordStart) {
    throw new Error('Unexpected Wouxun KG-UV9 record marker.')
  }
  const encoded = await transport.readExact(header[3] + 1, 4000)
  const plain = new Uint8Array(encoded.length)
  let previous = 0x52
  for (let index = 0; index < encoded.length; index += 1) {
    plain[index] = previous ^ encoded[index]
    previous = encoded[index]
  }
  const expected = checksum4(
    Uint8Array.from([header[1], header[2], header[3], ...plain.slice(0, -1)]),
  )
  if (plain.at(-1) !== expected) throw new Error('Wouxun KG-UV9 checksum failed.')
  return { command: header[1], payload: plain.slice(0, -1) }
}

async function enter(
  profile: KGUV9DProfile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  for (let attempt = 0; attempt < 10; attempt += 1) {
    await writeRecord(profile, transport, CMD_IDENT)
    try {
      const response = await readRecord(profile, transport)
      if (
        response.command === CMD_IDENT &&
        response.payload.length === 74 &&
        new TextDecoder().decode(response.payload.slice(0, 7)) === 'KG-UV9D'
      ) {
        return
      }
    } catch {
      // Retry transient identity frames.
    }
  }
  throw new Error(`The radio did not identify as ${profile.model}.`)
}

export async function readKGUV9DRadioWithTransport(
  profile: KGUV9DProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const blockSize = 64
    const total = MEMORY_SIZE / blockSize
    for (let index = 0; index < total; index += 1) {
      const address = index * blockSize
      const payload = Uint8Array.of(
        (address >>> 8) & 0xff,
        address & 0xff,
        blockSize,
      )
      await writeRecord(profile, transport, CMD_READ_CONFIG, payload)
      const response = await readRecord(profile, transport)
      if (
        response.command !== CMD_READ_CONFIG ||
        ((response.payload[0] << 8) | response.payload[1]) !== address
      ) {
        throw new Error(`Unexpected KG-UV9 config reply at 0x${address.toString(16)}.`)
      }
      image.set(response.payload.slice(2), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading Wouxun KG-UV9 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await writeRecord(profile, transport, CMD_HANGUP)
  }
}

export async function writeKGUV9DRadioWithTransport(
  profile: KGUV9DProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than KG-UV9 memory.')
  }
  await enter(profile, transport)
  try {
    const writes: Array<{ address: number; size: number }> = []
    for (const [start, size, count] of WRITE_RANGES) {
      for (let index = 0; index < count; index += 1) {
        writes.push({ address: start + index * size, size })
      }
    }
    for (let index = 0; index < writes.length; index += 1) {
      const { address, size } = writes[index]
      const payload = new Uint8Array(2 + size)
      payload[0] = (address >>> 8) & 0xff
      payload[1] = address & 0xff
      payload.set(image.slice(address, address + size), 2)
      await writeRecord(profile, transport, CMD_WRITE_CONFIG, payload)
      const response = await readRecord(profile, transport)
      if (
        response.command !== CMD_WRITE_CONFIG ||
        ((response.payload[0] << 8) | response.payload[1]) !== address
      ) {
        throw new Error(`Wouxun did not acknowledge block 0x${address.toString(16)}.`)
      }
      onProgress?.({
        current: index + 1,
        total: writes.length,
        label: 'Writing Wouxun KG-UV9 memory',
      })
    }
  } finally {
    await writeRecord(profile, transport, CMD_HANGUP)
  }
}
